import { z } from "zod";

/**
 * Shared DTO cho hồ sơ người dùng hiện tại — GET|PATCH /v1/me (US-E2.F7-0014 / @UC0001).
 *
 * Một nguồn sự thật cho api (validate biên + shape response) và mobile (gọi + map),
 * tránh drift (DRY). C5: schema validate tại biên tái dùng được.
 *
 * Cross-check API_BUILD_PLAN Phase 2: path canonical = `/v1/me` (KHÔNG /v1/users/me).
 * Slice 0014 = TỐI THIỂU: chỉ profile cơ bản + cờ onboardingCompleted. 0015/0016 bồi đắp.
 *
 * Bảo mật (C2/C3): response KHÔNG mang passwordHash/secret/PII thừa — chỉ field cần cho UI.
 */

/**
 * Shape response cho GET /v1/me và PATCH /v1/me.
 * email/fullName/avatarUrl/emailVerifiedAt NULLABLE (user social chưa có email; chưa verify).
 * onboardingCompleted = cờ đã xem onboarding lần đầu chưa (nguồn sự thật server — SD-4).
 */
export const meResponseSchema = z.object({
  id: z.string().uuid(),
  email: z.string().email().nullable(),
  fullName: z.string().nullable(),
  // M2: avatarUrl phải là URL hợp lệ (https R2/CDN theo SD-1) khi có; null khi chưa đặt.
  avatarUrl: z.string().url().nullable(),
  // ISO-8601 string khi serialize qua JSON; null khi chưa verify email.
  emailVerifiedAt: z.string().datetime().nullable(),
  onboardingCompleted: z.boolean(),
});

export type MeResponse = z.infer<typeof meResponseSchema>;

/** Giới hạn độ dài tên hiển thị (US-E3.F1-0016 / @UC0007 — invariant story ≤30 ký tự). */
export const FULL_NAME_MAX_LENGTH = 30;

/**
 * Body cho PATCH /v1/me.
 * - onboardingCompleted OPTIONAL: client gửi `true` khi hoàn thành onboarding (AC4, slice 0014).
 * - fullName OPTIONAL (US-E3.F1-0016 / @UC0007): tên hiển thị. Trim trước rồi validate
 *   1..30 ký tự — rỗng/chỉ-khoảng-trắng → reject (AC3); >30 → reject (AC2). Đếm SAU trim.
 * - email KHÔNG có ở đây ⇒ `.strict()` reject (AC5: email bất biến qua endpoint profile).
 * - `.strict()` chặn field lạ tại biên (chống IDOR/over-post: KHÔNG nhận id/email/role từ body).
 */
export const patchMeRequestSchema = z
  .object({
    onboardingCompleted: z.boolean().optional(),
    fullName: z
      .string()
      .trim()
      .min(1, "Tên hiển thị không được để trống")
      .max(FULL_NAME_MAX_LENGTH, "Tên hiển thị tối đa 30 ký tự")
      .optional(),
  })
  .strict();

export type PatchMeRequest = z.infer<typeof patchMeRequestSchema>;
